import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { LazyIconCalendar } from '@/design-system/atoms/Icons/IconCalendar/Lazy';
import { LazyIconPhone } from '@/design-system/atoms/Icons/IconPhone/Lazy';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { AvailabilityCalendar } from '@/design-system/molecules/AvailabilityCalendar';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { usePhysicianBioAvailabilityDate } from '@/hooks/usePhysicianBioAvailabilityDate';
import { tokenReplace } from '@/utils/tokenReplace';

import styles from './AvailabilityCard.module.scss';

/** Availability copy for a Virtual Care slot inside the "now" window. */
const AVAILABLE_NOW_TEXT = 'Available Now';

/** Authored-equivalent label prefixing a date/time availability value. */
const AVAILABILITY_PREFIX_TEXT = 'Available as soon as: ';

/** Schedule CTA label for a Virtual Care provider with online scheduling enabled. */
const VIRTUAL_CARE_CTA_TEXT = 'Schedule Your Virtual Visit';

const formatAvailabilityDate = (date) => (date ? moment(date).format('ddd MMM DD') : '');

/**
 * Resolves the availability message rendered in place of the calendar carousel when
 * `disableCalendar` is on. Mirrors `PhysicianBioHero`'s `getAvailabilityMessage`
 * (`PhysicianBioHero.js:59-99`) exactly.
 *
 * `onlineBookingAvailable` and `availableSoonOnline` are both master gates: when
 * either is false this returns `null` without consulting `hasVirtualCare` or
 * `category`, so no promissory availability copy renders. Beneath those gates, every
 * provider (Virtual Care or not) reaches the same tiered `category` switch - a time
 * when available today, a date when available a future day, nothing when `category`
 * is `"none"`. `hasVirtualCare` only selects the `"now"` bucket's wording: "Available
 * Now" for Virtual Care, the actual time for everyone else.
 *
 * @param {Object} params
 * @param {boolean} params.availableSoonOnline Whether the provider has availability soon.
 * @param {boolean} params.onlineBookingAvailable Whether online scheduling is enabled.
 * @param {boolean} params.hasVirtualCare Whether the provider offers Virtual Care.
 * @param {('now'|'soon-time'|'soon-day'|'none')} params.category Availability category
 *     derived by `usePhysicianBioAvailabilityDate`.
 * @param {?string} params.nextAvailableTime Formatted next slot time, e.g. `'3:30pm'`.
 * @param {?string} params.availabilityDate Next available date as `YYYY-MM-DD`.
 * @returns {?{prefix: ?string, value: string, valueVariant: string}} The message's
 *     optional prefix, its value, and the `Text` variant that value renders at, or
 *     `null` when no availability message renders.
 */
const getAvailabilityMessage = ({
    availableSoonOnline,
    onlineBookingAvailable,
    hasVirtualCare,
    category,
    nextAvailableTime,
    availabilityDate,
}) => {
    if (!onlineBookingAvailable || !availableSoonOnline) {
        return null;
    }

    switch (category) {
        case 'now':
            // "Available Now" is Virtual-Care-only; a non-virtual provider available
            // within the hour still shows the time, not the "now" wording.
            return hasVirtualCare
                ? { prefix: null, value: AVAILABLE_NOW_TEXT, valueVariant: Text.VARIANT.T2 }
                : nextAvailableTime
                  ? {
                        prefix: AVAILABILITY_PREFIX_TEXT,
                        value: nextAvailableTime,
                        valueVariant: Text.VARIANT.T2B,
                    }
                  : null;
        case 'soon-time':
            return {
                prefix: AVAILABILITY_PREFIX_TEXT,
                value: nextAvailableTime,
                valueVariant: Text.VARIANT.T2B,
            };
        case 'soon-day':
            return availabilityDate
                ? {
                      prefix: AVAILABILITY_PREFIX_TEXT,
                      value: formatAvailabilityDate(availabilityDate),
                      valueVariant: Text.VARIANT.T2B,
                  }
                : null;
        default:
            return null;
    }
};

const AvailabilityCard = ({
    clickToCallLabel,
    notAcceptingPatientsText = 'Currently not accepting new patients',
    existingPatientText,
    noOnlineSchedulingText,
    availabilityLabel = 'View All Times & Schedule',
    onlineBookingAvailable,
    patientType,
    acceptingNewPatients,
    myChartUrl,
    schedulingCta,
    phoneNumber,
    npi,
    newPatientAppointmentIds,
    analytics,
    disableCalendar,
    departmentIds,
    eventHandlers = {},
    availableSoonOnline = false,
    availableOnline = false,
    firstAvailabilityDate = null,
    nextAvailabilityDate = null,
    hasVirtualCare = false,
    disableAvailabilityMessage = false,
}) => {
    const breakpoint = useBreakpoint();
    const isMobile = !breakpoint?.large;
    const simpleWidgetInstanceId = React.useId().replace(/[^a-zA-Z0-9]/g, '');

    const callNumber = phoneNumber === null ? '800-247-9580' : phoneNumber;
    const callLabel = clickToCallLabel || `${callNumber}`;

    // - showCalendar: only when availableSoonOnline=true (appointments within 180 days)
    // - hasOnlineAvailability: when availableSoonOnline OR availableOnline (appointments exist)
    const showCalendar = availableSoonOnline;
    const hasOnlineAvailability = availableSoonOnline || availableOnline;

    // The new-patient online-scheduling branch this feature adds to (mirrors the JSX
    // condition below) - the availability message/calendar mode only applies here.
    const isNewPatientOnlineBranch =
        onlineBookingAvailable &&
        patientType === 'new' &&
        acceptingNewPatients &&
        hasOnlineAvailability;
    // Only fetch when the calendar-disabled availability message will actually render;
    // passing `npi: undefined` short-circuits the hook's own fetch effect (rules of
    // hooks require the hook itself to be called unconditionally on every render).
    const shouldFetchAvailabilityMessage =
        !disableAvailabilityMessage && isNewPatientOnlineBranch && disableCalendar;

    const {
        availabilityDate: virtualCareAvailabilityDate,
        category: virtualCareCategory,
        nextAvailableTime: virtualCareNextAvailableTime,
    } = usePhysicianBioAvailabilityDate({
        npi: shouldFetchAvailabilityMessage ? npi : undefined,
        newPatientAppointmentIds,
        firstAvailabilityDate,
        nextAvailabilityDate,
    });
    const availabilityMessage = shouldFetchAvailabilityMessage
        ? getAvailabilityMessage({
              availableSoonOnline,
              onlineBookingAvailable,
              hasVirtualCare,
              category: virtualCareCategory,
              nextAvailableTime: virtualCareNextAvailableTime,
              availabilityDate: virtualCareAvailabilityDate,
          })
        : null;

    const scheduleCtaLabel = hasVirtualCare ? VIRTUAL_CARE_CTA_TEXT : availabilityLabel;

    const simpleWidgetContainerId = `mychart-simple-${(npi || 'provider').replace(
        /[^a-zA-Z0-9]/g,
        '',
    )}-${simpleWidgetInstanceId}`;

    const analyticsObj = React.useMemo(() => {
        return {
            ...analytics,
        };
    }, [analytics]);
    return (
        <Theme name={Theme.NAME.TREATMENTS_AND_SERVICES} className={styles[`availability-card`]}>
            {!onlineBookingAvailable && (
                <div className={styles[`availability-card__content`]}>
                    {noOnlineSchedulingText && (
                        <RichTextEditor
                            text={tokenReplace(noOnlineSchedulingText, { phone: callNumber })}
                        />
                    )}
                    <Button
                        className={noOnlineSchedulingText && styles[`availability-card__button`]}
                        label={callLabel}
                        iconComponent={LazyIconPhone}
                        href={`tel:${callNumber}`}
                        analytics={{
                            ...analyticsObj,
                            interactionType: 'fad_result_primary_cta',
                        }}
                        {...eventHandlers}
                    />
                </div>
            )}
            {onlineBookingAvailable && patientType === 'existing' && (
                <div className={styles[`availability-card__content`]}>
                    {existingPatientText ? (
                        <RichTextEditor
                            text={tokenReplace(existingPatientText, {
                                MyChart: myChartUrl
                                    ? `<a href="${myChartUrl}" target="_blank">MyChart</a>`
                                    : 'MyChart',
                            })}
                            analytics={{
                                ...analyticsObj,
                                interactionType: 'fad_result_cta',
                            }}
                            onAnchorClick={eventHandlers.onAnchorClick}
                        />
                    ) : (
                        <div>
                            <Text
                                content="Schedule appointment in "
                                variant={Text.VARIANT.T2}
                                tag={Text.TAG.SPAN}
                                className={styles[`availability-card__text`]}
                            />
                            <Button
                                label="MyChart"
                                buttonStyle={Button.STYLE.TEXT}
                                href={myChartUrl}
                                target={Button.TARGET.BLANK}
                                className={styles[`availability-card__button--text`]}
                                analytics={{
                                    ...analyticsObj,
                                    interactionType: 'fad_result_cta',
                                }}
                                {...eventHandlers}
                            />
                            {!isMobile && <br />}
                            <Text
                                content=" or "
                                variant={Text.VARIANT.T2}
                                className={styles[`availability-card__text`]}
                            />
                        </div>
                    )}
                    <Button
                        className={styles[`availability-card__button`]}
                        label={callLabel}
                        iconComponent={LazyIconPhone}
                        href={`tel:${callNumber}`}
                        analytics={{
                            ...analyticsObj,
                            interactionType: 'fad_result_primary_cta',
                        }}
                        {...eventHandlers}
                    />
                </div>
            )}
            {onlineBookingAvailable &&
                patientType === 'new' &&
                acceptingNewPatients &&
                hasOnlineAvailability && (
                    <div className={styles[`availability-card__availability`]}>
                        <div className="fad-availability-simple-widget" hidden aria-hidden="true">
                            <div id={simpleWidgetContainerId} />
                        </div>
                        {/* AHSMKTG-3534: Calendar only shows when availableSoonOnline=true */}
                        {!disableCalendar && npi && showCalendar && (
                            <AvailabilityCalendar
                                npi={npi}
                                newPatientAppointmentIds={newPatientAppointmentIds}
                                analytics={{
                                    ...analyticsObj,
                                }}
                                key={npi}
                                departmentIds={departmentIds}
                                firstAvailabilityDate={firstAvailabilityDate}
                                nextAvailabilityDate={nextAvailabilityDate}
                            />
                        )}
                        {/* AHSMKTG-4010: disableCalendar swaps the carousel for a tiered
                            (Virtual Care) or date-only (non-Virtual-Care) message. */}
                        {disableCalendar && availabilityMessage && (
                            <p className={styles[`availability-card__availability-text`]}>
                                {availabilityMessage.prefix && (
                                    <Text
                                        content={`${availabilityMessage.prefix} `}
                                        tag={Text.TAG.SPAN}
                                        variant={Text.VARIANT.T2}
                                    />
                                )}
                                <Text
                                    content={availabilityMessage.value}
                                    tag={Text.TAG.SPAN}
                                    variant={availabilityMessage.valueVariant}
                                />
                            </p>
                        )}
                        <Button
                            label={scheduleCtaLabel}
                            iconComponent={LazyIconCalendar}
                            {...schedulingCta}
                            analytics={{
                                ...analyticsObj,
                                interactionType: 'fad_result_primary_cta',
                            }}
                            {...eventHandlers}
                        />
                    </div>
                )}
            {/* AHSMKTG-3534: Online booking available but no availability slots - show phone CTA */}
            {onlineBookingAvailable &&
                patientType === 'new' &&
                acceptingNewPatients &&
                !hasOnlineAvailability && (
                    <div className={styles[`availability-card__content`]}>
                        {noOnlineSchedulingText && (
                            <RichTextEditor
                                text={tokenReplace(noOnlineSchedulingText, { phone: callNumber })}
                            />
                        )}
                        <Button
                            label={callLabel}
                            iconComponent={LazyIconPhone}
                            href={`tel:${callNumber}`}
                            analytics={{
                                ...analyticsObj,
                                interactionType: 'fad_result_primary_cta',
                            }}
                            {...eventHandlers}
                        />
                    </div>
                )}
            {onlineBookingAvailable && patientType === 'new' && !acceptingNewPatients && (
                <div className={styles[`availability-card__content`]}>
                    {notAcceptingPatientsText && (
                        <div>
                            <RichTextEditor text={notAcceptingPatientsText} />
                        </div>
                    )}
                    <Button
                        className={styles[`availability-card__button`]}
                        label={callLabel}
                        iconComponent={LazyIconPhone}
                        href={`tel:${callNumber}`}
                        analytics={{
                            ...analyticsObj,
                            interactionType: 'fad_result_primary_cta',
                        }}
                        {...eventHandlers}
                    />
                </div>
            )}
        </Theme>
    );
};

AvailabilityCard.propTypes = {
    /**
     * Text to display for the "Click to Call" button
     */
    clickToCallLabel: PropTypes.string,
    /**
     * Text overwritten for the "View All Times & Schedule" button
     */
    availabilityLabel: PropTypes.string,
    /**
     * Text to display when the provider is not accepting new patients
     */
    notAcceptingPatientsText: PropTypes.string,
    /**
     * Text to display when the patient is an existing patient
     */
    existingPatientText: PropTypes.string,
    /**
     * Text to display when online booking is not available
     */
    noOnlineSchedulingText: PropTypes.string,
    /**
     * Whether online booking is available
     */
    onlineBookingAvailable: PropTypes.bool,
    /**
     * Type of patient
     */
    patientType: PropTypes.oneOf(['new', 'existing']),
    /**
     * Whether the provider is accepting new patients
     */
    acceptingNewPatients: PropTypes.bool,
    /**
     * Phone number of the provider
     */
    phoneNumber: PropTypes.string,
    /**
     * URL to MyChart
     */
    myChartUrl: PropTypes.string,
    /**
     * schedulingCta
     */
    schedulingCta: PropTypes.shape(Button.propTypes),
    /**
     * provider npi
     */
    npi: PropTypes.string,
    /**
     * first available appointment date
     */
    availabilityDate: PropTypes.string,
    /**
     * Visit type IDs - Authored value
     */
    newPatientAppointmentIds: PropTypes.arrayOf(PropTypes.string),
    /**
     * Analytics object
     */
    analytics: PropTypes.object,
    /**
     * AHSMKTG-3534: Has appointments within 180 days (shows calendar widget)
     */
    availableSoonOnline: PropTypes.bool,
    /**
     * AHSMKTG-3534: Has appointments within 545 days (shows schedule CTA)
     */
    availableOnline: PropTypes.bool,
    /**
     * AHSMKTG-3534: First available appointment date (YYYY-MM-DD) - calendar starts from this date
     */
    firstAvailabilityDate: PropTypes.string,
    /**
     * AHSMKTG-3534: Next available appointment date (YYYY-MM-DD) - used as fallback for calendar start
     */
    nextAvailabilityDate: PropTypes.string,
    /**
     * AHSMKTG-4010: Whether the provider offers Virtual Care. Selects the availability
     * message's content (tiered vs. date-only) and the schedule CTA's label - never
     * gates visibility of the message, carousel, or CTA.
     */
    hasVirtualCare: PropTypes.bool,
    /**
     * When true, prevents the availability message fetch from running and suppresses
     * the availability message <p> block. Schedule CTA and phone CTA are unaffected.
     */
    disableAvailabilityMessage: PropTypes.bool,
};

export default AvailabilityCard;
